import { useMap } from 'ms-map-gl'
import { useEffect, useState } from 'react'
import {
  CLUSTER_SOURCE_ID,
  CLUSTER_LAYER_ID,
  CLUSTER_SPIDERFY_ZOOM_LEVEL
} from '~map-preview/constants'
import { FeatureCollection } from 'geojson'
import { ClusterSpiderfyComponent } from './Component'

function ClusterZoomPlugin() {
  const mapGl = useMap()
  const map = mapGl.current
  const [spiderfiedPoints, setSpiderfiedPoints] =
    useState<FeatureCollection | null>()
  const [spiderfiedLines, setSpiderfiedLines] =
    useState<FeatureCollection | null>()

  useEffect(() => {
    if (!map) return

    const handleClick = async (event) => {
      const cluster = event.features[0]
      const clusterCenter = cluster.geometry.coordinates
      const zoom = map.getZoom()

      const source = map.getSource(CLUSTER_SOURCE_ID) as any
      if (!source) return

      if (zoom >= CLUSTER_SPIDERFY_ZOOM_LEVEL) {
        const points = await source.getClusterLeaves(
          cluster.properties.cluster_id,
          500,
          0
        )
        spiderfy(clusterCenter, points)
      } else {
        let zoomLevel = await source.getClusterExpansionZoom(cluster.id)

        if (zoomLevel > CLUSTER_SPIDERFY_ZOOM_LEVEL)
          zoomLevel = CLUSTER_SPIDERFY_ZOOM_LEVEL

        map.easeTo({ center: clusterCenter, zoom: zoomLevel })
      }
    }

    map.on('click', CLUSTER_LAYER_ID, handleClick)
    map.on('zoomend', () => {
      const currentZoom = map.getZoom()
      if (currentZoom < CLUSTER_SPIDERFY_ZOOM_LEVEL) {
        unspiderfy()
      }
    })
    return () => {
      map.off('click', CLUSTER_LAYER_ID, handleClick)
    }
  }, [map])

  const unspiderfy = () => {
    setSpiderfiedLines(null)
    setSpiderfiedPoints(null)
  }

  const spiderfy = (center, points) => {
    if (!map) return
    const centerPx = map.project(center)
    const angleStep = (2 * Math.PI) / points.length
    const radiusPx = 100

    const newSpiderfiedPoints = points.map((point, i) => {
      const angle = i * angleStep
      const x = centerPx.x + radiusPx * Math.cos(angle)
      const y = centerPx.y + radiusPx * Math.sin(angle)
      const coords = map.unproject([x, y])

      return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [coords.lng, coords.lat] },
        properties: point.properties
      }
    })

    const newSpiderfiedLines = newSpiderfiedPoints.map((point) => ({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: [center, point.geometry.coordinates]
      },
      properties: point.properties
    }))

    setSpiderfiedPoints({
      type: 'FeatureCollection',
      features: newSpiderfiedPoints
    })
    setSpiderfiedLines({
      type: 'FeatureCollection',
      features: newSpiderfiedLines
    })
  }

  if (!spiderfiedLines || !spiderfiedPoints) return null

  return (
    <ClusterSpiderfyComponent
      pointsGeojson={spiderfiedPoints}
      linesGeojson={spiderfiedLines}
    />
  )
}

export { ClusterZoomPlugin }
